import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, ArrowRight } from 'lucide-react';
import AuthShell from '../../components/auth/AuthShell';
import { AuthHeading, PrimaryButton } from '../../components/auth/AuthPrimitives';

const FONT = "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif";

// Two Meduri tiers, faithful to docs/design/auth-subscription-prototype/screens/PlansScreen.jsx.
// Pilot (free, no card) -> trial-success; Practice ($29.99/mo) -> real Stripe payment.
const PLANS = [
  {
    id: 'pilot',
    name: 'Pilot',
    eyebrow: 'Free trial',
    price: '$0',
    cadence: '',
    tagline: 'Your first 10 appeals, on us. No credit card, no clearinghouse setup, no call with sales.',
    features: [
      '10 free generated appeal letters',
      'Upload denial letters & ERAs',
      'Export PDF or send via fax',
      'Email support',
    ],
    cta: 'Start Pilot',
    popular: false,
  },
  {
    id: 'practice',
    name: 'Practice',
    eyebrow: 'Most popular',
    price: '$29.99',
    cadence: '/ month',
    tagline: 'Unlimited appeals for your clinic. A flat monthly fee — no per-appeal charges, no surprise bills.',
    features: [
      'Everything in Pilot',
      'Unlimited generated appeals',
      'HIPAA-compliant fax included',
      'Outcome & deadline tracking',
      'Letter of medical necessity generator',
    ],
    cta: 'Choose Practice',
    popular: true,
  },
];

function PlanCard({ plan, onPick }) {
  const [hovered, setHovered] = useState(false);
  const popular = plan.popular;
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        border: popular ? '2px solid #000' : '1px solid #000',
        background: '#fff',
        padding: 28,
        display: 'flex',
        flexDirection: 'column',
        position: 'relative',
        transition: 'transform 0.15s, box-shadow 0.15s',
        transform: hovered ? 'translateY(-2px)' : 'none',
        boxShadow: hovered ? '0 4px 16px rgba(0,0,0,0.08)' : 'none',
      }}
    >
      {popular ? (
        <div style={{ position: 'absolute', top: -12, left: 24, background: '#000', color: '#fff', fontFamily: FONT, fontSize: 11, fontWeight: 700, padding: '4px 10px', letterSpacing: '0.06em', textTransform: 'uppercase' }}>
          {plan.eyebrow}
        </div>
      ) : (
        <div style={{ fontFamily: FONT, fontSize: 11, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#64748B', marginBottom: 8 }}>
          {plan.eyebrow}
        </div>
      )}
      <h2 style={{ fontFamily: FONT, fontSize: 28, fontWeight: 700, margin: 0, lineHeight: 1, color: '#000' }}>{plan.name}</h2>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 16 }}>
        <span style={{ fontFamily: FONT, fontSize: 48, fontWeight: 700, lineHeight: 1, color: '#000', letterSpacing: '-0.02em' }}>{plan.price}</span>
        {plan.cadence && <span style={{ fontFamily: FONT, fontSize: 14, color: '#64748B' }}>{plan.cadence}</span>}
      </div>
      <p style={{ fontFamily: FONT, fontSize: 14, lineHeight: 1.5, color: '#1D2433', marginTop: 12, marginBottom: 24, textWrap: 'pretty' }}>{plan.tagline}</p>
      <div style={{ height: 0, borderTop: '0.4px solid #000', marginBottom: 20 }} />
      <ul style={{ listStyle: 'none', padding: 0, margin: '0 0 28px', display: 'flex', flexDirection: 'column', gap: 12 }}>
        {plan.features.map((f) => (
          <li key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontFamily: FONT, fontSize: 14, lineHeight: 1.45, color: '#000' }}>
            <span style={{ color: '#000', flexShrink: 0, marginTop: 2, display: 'inline-flex' }}><Check size={14} /></span>
            <span>{f}</span>
          </li>
        ))}
      </ul>
      <div style={{ marginTop: 'auto' }}>
        {popular ? (
          <PrimaryButton onClick={onPick} icon={ArrowRight}>{plan.cta}</PrimaryButton>
        ) : (
          <button
            type="button"
            onClick={onPick}
            style={{ fontFamily: FONT, fontSize: 14, fontWeight: 600, width: '100%', background: '#fff', color: '#000', border: '1px solid #000', padding: '14px 20px', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 10 }}
          >
            {plan.cta}
            <ArrowRight size={16} />
          </button>
        )}
      </div>
    </div>
  );
}

export default function PlansPage() {
  const navigate = useNavigate();

  function pick(id) {
    if (id === 'pilot') navigate('/signup/success?plan=pilot');
    else navigate('/payment');
  }

  return (
    <AuthShell onLogoClick={() => navigate('/login')} wide>
      <AuthHeading
        eyebrow="Step 3 of 3 · Pricing"
        title="Honest, early-stage pricing"
        subtitle="Try it for free on your first ten denials. After that, pay a flat monthly fee — or stay on the free pilot."
      />
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20 }}>
        {PLANS.map((p) => (
          <PlanCard key={p.id} plan={p} onPick={() => pick(p.id)} />
        ))}
      </div>
      <div style={{ marginTop: 24, fontFamily: FONT, fontSize: 12, color: '#64748B', textAlign: 'center' }}>
        Cancel anytime. Prices in USD.
      </div>
      <div style={{ marginTop: 12, textAlign: 'center' }}>
        <a onClick={() => navigate('/overview')} style={{ fontFamily: FONT, fontSize: 13, color: '#64748B', textDecoration: 'underline', cursor: 'pointer' }}>
          Skip for now
        </a>
      </div>
    </AuthShell>
  );
}
